import React, {
  createContext, useContext, useState, useEffect, useRef, useCallback,
} from 'react';
import axios from 'axios';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UserProfile {
  userId: string;
  email: string;
  displayName: string;
  roles: string[];
}

interface TokenResponse {
  access_token: string;
  id_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserProfile | null;
  accessToken: string | null;
  error: string | null;
  requiresPasswordChange: boolean;
  passwordExpired: boolean;
  ssprStep: 'otp' | 'newPassword' | null;
  ssprCodeSentTo: string | null;
  signIn: (username: string, password: string) => Promise<void>;
  signInWithBarcode: (barcode: string, pin: string) => Promise<void>;
  signOut: () => void;
  submitNewPassword: (newPassword: string, confirmPassword: string) => Promise<void>;
  initiatePasswordReset: (email: string) => Promise<void>;
  verifyResetCode: (code: string) => Promise<void>;
  completePasswordReset: (newPassword: string, confirmPassword: string) => Promise<void>;
  clearError: () => void;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  accessToken: null,
  error: null,
  requiresPasswordChange: false,
  passwordExpired: false,
  ssprStep: null,
  ssprCodeSentTo: null,
  signIn: async () => {},
  signInWithBarcode: async () => {},
  signOut: () => {},
  submitNewPassword: async () => {},
  initiatePasswordReset: async () => {},
  verifyResetCode: async () => {},
  completePasswordReset: async () => {},
  clearError: () => {},
});

export const useAuthContext = (): AuthContextValue => useContext(AuthContext);

// ─── Type helpers ─────────────────────────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// ─── JWT expiry helpers (used by session storage below) ──────────────────────

function parseJwt(token: string): Record<string, unknown> {
  try {
    const payload = token.split('.')[1];
    const parsed: unknown = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return isRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function tokenExpiresAtMs(token: string): number {
  const claims = parseJwt(token);
  return typeof claims.exp === 'number' ? claims.exp * 1000 : 0;
}

export function isTokenExpired(token: string): boolean {
  const exp = tokenExpiresAtMs(token);
  return exp > 0 && Date.now() >= exp;
}

// ─── Session storage ──────────────────────────────────────────────────────────

const STORAGE_USER_KEY = 'jems_auth_user';
const STORAGE_TOKEN_KEY = 'jems_auth_token';
const STORAGE_REFRESH_KEY = 'jems_auth_refresh';

const saveSession = (user: UserProfile, token: string, refreshToken: string) => {
  localStorage.setItem(STORAGE_USER_KEY, JSON.stringify(user));
  localStorage.setItem(STORAGE_TOKEN_KEY, token);
  if (refreshToken) {
    localStorage.setItem(STORAGE_REFRESH_KEY, refreshToken);
  } else {
    localStorage.removeItem(STORAGE_REFRESH_KEY);
  }
};

const clearSession = () => {
  localStorage.removeItem(STORAGE_USER_KEY);
  localStorage.removeItem(STORAGE_TOKEN_KEY);
  localStorage.removeItem(STORAGE_REFRESH_KEY);
};

const loadSession = (): { user: UserProfile; token: string; refreshToken: string } | null => {
  try {
    const raw = localStorage.getItem(STORAGE_USER_KEY);
    const token = localStorage.getItem(STORAGE_TOKEN_KEY);
    const refreshToken = localStorage.getItem(STORAGE_REFRESH_KEY) ?? '';
    if (raw && token && !isTokenExpired(token)) {
      const parsed: unknown = JSON.parse(raw);
      if (isRecord(parsed)) {
        const user: UserProfile = {
          userId: String(parsed.userId ?? ''),
          email: String(parsed.email ?? ''),
          displayName: String(parsed.displayName ?? ''),
          roles: Array.isArray(parsed.roles) ? parsed.roles.filter((r): r is string => typeof r === 'string') : [],
        };
        return { user, token, refreshToken };
      }
    }
  } catch {
    clearSession();
  }
  return null;
};

// ─── JWT helpers ──────────────────────────────────────────────────────────────

function extractRoles(claims: Record<string, unknown>): string[] {
  const r = claims.roles;
  if (Array.isArray(r)) return r.filter((x): x is string => typeof x === 'string');
  if (typeof r === 'string' && r) return [r];
  return [];
}

function buildProfile(tokens: TokenResponse): UserProfile {
  const idClaims = parseJwt(tokens.id_token);
  const accessClaims = parseJwt(tokens.access_token);
  // Access token carries the full app role set that the backend validates against;
  // fall back to id_token roles if access token has none.
  // JwtSecurityTokenHandler serializes a single role as a string scalar, not an array,
  // so extractRoles handles both forms.
  const accessRoles = extractRoles(accessClaims);
  const idRoles = extractRoles(idClaims);
  return {
    userId: String(idClaims.oid ?? idClaims.sub ?? ''),
    email: String(idClaims.email ?? idClaims.preferred_username ?? ''),
    displayName: String(idClaims.name ?? idClaims.email ?? ''),
    roles: accessRoles.length > 0 ? accessRoles : idRoles,
  };
}

// ─── Proxy fetch helpers ──────────────────────────────────────────────────────

function toTokenResponse(data: Record<string, unknown>): TokenResponse {
  return {
    access_token: String(data.access_token ?? ''),
    id_token: String(data.id_token ?? ''),
    refresh_token: String(data.refresh_token ?? ''),
    expires_in: typeof data.expires_in === 'number' ? data.expires_in : 0,
    token_type: String(data.token_type ?? ''),
  };
}

async function postProxy(endpoint: string, body: Record<string, unknown>): Promise<Record<string, unknown>> {
  let res: Response;
  try {
    res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error('Unable to reach the server. Please check your connection and try again.');
  }
  let json: unknown;
  try {
    json = await res.json();
  } catch {
    throw new Error('Unexpected server response. Please try again.');
  }
  if (!res.ok) {
    throw new Error(isRecord(json) ? String(json.error ?? 'Request failed') : 'Request failed');
  }
  return isRecord(json) ? json : {};
}

async function loginViaProxy(
  email: string,
  password: string,
): Promise<TokenResponse | { requiresPasswordChange: true; continuation_token: string } | { passwordExpired: true }> {
  const json = await postProxy('/Auth/Login', { email, password });
  if (json.requiresPasswordChange === true) {
    return { requiresPasswordChange: true, continuation_token: String(json.continuation_token ?? '') };
  }
  if (json.passwordExpired === true) {
    return { passwordExpired: true };
  }
  return toTokenResponse(json);
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  const [passwordExpired, setPasswordExpired] = useState(false);
  const [ssprStep, setSsprStep] = useState<'otp' | 'newPassword' | null>(null);
  const [ssprCodeSentTo, setSsprCodeSentTo] = useState<string | null>(null);

  const pendingContinuationToken = useRef<string | null>(null);
  const ssprPendingEmail = useRef<string | null>(null);
  const ssprContinuationToken = useRef<string | null>(null);

  useEffect(() => {
    const stored = loadSession();
    if (stored) {
      // Set the axios header before any state update so that EmployeeProvider (mounted
      // at the app root) doesn't fire its first SWR fetch without a token.
      axios.defaults.headers.common.Authorization = `Bearer ${stored.token}`;
      setUser(stored.user);
      setAccessToken(stored.token);
      setRefreshToken(stored.refreshToken || null);
    }
    setIsLoading(false);
  }, []);

  // Skip the first run: on mount this effect fires in the same passive-effect flush as the
  // session-restore effect above, with a stale (pre-restore) accessToken closure, and would
  // otherwise immediately delete the header that effect just set.
  const isInitialAccessTokenEffect = useRef(true);
  useEffect(() => {
    if (isInitialAccessTokenEffect.current) {
      isInitialAccessTokenEffect.current = false;
      return;
    }
    if (accessToken) {
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    } else {
      delete axios.defaults.headers.common.Authorization;
    }
  }, [accessToken]);

  const applyTokens = useCallback((tokens: TokenResponse) => {
    const profile = buildProfile(tokens);
    // Set the axios header before any state update so that EmployeeProvider (mounted
    // at the app root) doesn't fire its first SWR fetch without a token.
    if (tokens.access_token) {
      axios.defaults.headers.common.Authorization = `Bearer ${tokens.access_token}`;
    }
    setUser(profile);
    setAccessToken(tokens.access_token);
    setRefreshToken(tokens.refresh_token || null);
    saveSession(profile, tokens.access_token, tokens.refresh_token ?? '');
    setRequiresPasswordChange(false);
    setPasswordExpired(false);
    setSsprStep(null);
    setSsprCodeSentTo(null);
    pendingContinuationToken.current = null;
    ssprPendingEmail.current = null;
    ssprContinuationToken.current = null;
  }, []);

  // Renew the session before the access token expires. Entra logins carry a
  // refresh token; barcode logins don't (their backend-issued token already
  // lasts a full shift), so without one we sign out at expiry so the app
  // doesn't get stuck on the "Logging in..." spinner with a stale session.
  useEffect(() => {
    if (!accessToken) return undefined;
    const expiresAt = tokenExpiresAtMs(accessToken);
    if (expiresAt - Date.now() <= 0) return undefined;

    const refreshLeadMs = 5 * 60 * 1000;
    const retryIntervalMs = 60 * 1000;
    let timer: ReturnType<typeof setTimeout>;
    let cancelled = false;

    const forceSignOut = () => {
      clearSession();
      setUser(null);
      setAccessToken(null);
      setRefreshToken(null);
    };

    const scheduleSignOutAtExpiry = () => {
      timer = setTimeout(() => {
        if (!cancelled) forceSignOut();
      }, Math.max(expiresAt - Date.now(), 0));
    };

    const attemptRefresh = async () => {
      try {
        const json = await postProxy('/Auth/Refresh', { refreshToken });
        if (!cancelled) applyTokens(toTokenResponse(json));
      } catch {
        if (cancelled) return;
        // Refresh failed (offline, service blip, …) — keep retrying while the
        // current token is still valid, then sign out at actual expiry.
        if (expiresAt - Date.now() > retryIntervalMs + 30 * 1000) {
          timer = setTimeout(() => { attemptRefresh().catch(() => undefined); }, retryIntervalMs);
        } else {
          scheduleSignOutAtExpiry();
        }
      }
    };

    if (refreshToken) {
      timer = setTimeout(() => { attemptRefresh().catch(() => undefined); }, Math.max(expiresAt - Date.now() - refreshLeadMs, 0));
    } else {
      scheduleSignOutAtExpiry();
    }

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [accessToken, refreshToken, applyTokens]);

  const signIn = useCallback(async (username: string, password: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await loginViaProxy(username, password);
      if ('requiresPasswordChange' in result) {
        pendingContinuationToken.current = result.continuation_token;
        setRequiresPasswordChange(true);
      } else if ('passwordExpired' in result) {
        ssprPendingEmail.current = username;
        setPasswordExpired(true);
      } else {
        applyTokens(result);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign-in failed. Check your email and password.');
    } finally {
      setIsLoading(false);
    }
  }, [applyTokens]);

  const signInWithBarcode = useCallback(async (barcode: string, pin: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const json = await postProxy('/Auth/LoginWithBarcode', { barcode, pin });
      applyTokens(toTokenResponse(json));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign-in failed. Check your barcode and PIN.');
    } finally {
      setIsLoading(false);
    }
  }, [applyTokens]);

  const submitNewPassword = useCallback(async (newPassword: string, confirmPassword: string): Promise<void> => {
    if (!pendingContinuationToken.current) return;
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const json = await postProxy('/Auth/ChangePassword', {
        continuationToken: pendingContinuationToken.current,
        newPassword,
      });
      applyTokens(toTokenResponse(json));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Password change failed.');
    } finally {
      setIsLoading(false);
    }
  }, [applyTokens]);

  const initiatePasswordReset = useCallback(async (email: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const json = await postProxy('/Auth/InitiatePasswordReset', { email });
      ssprPendingEmail.current = email;
      ssprContinuationToken.current = String(json.continuationToken ?? '');
      setSsprCodeSentTo(String(json.sentTo ?? email));
      setSsprStep('otp');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send reset code.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const verifyResetCode = useCallback(async (code: string): Promise<void> => {
    if (!ssprContinuationToken.current) return;
    setIsLoading(true);
    setError(null);
    try {
      const json = await postProxy('/Auth/VerifyResetCode', {
        continuationToken: ssprContinuationToken.current,
        code,
      });
      ssprContinuationToken.current = String(json.continuationToken ?? '');
      setSsprStep('newPassword');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Invalid code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const completePasswordReset = useCallback(async (newPassword: string, confirmPassword: string): Promise<void> => {
    if (!ssprContinuationToken.current || !ssprPendingEmail.current) return;
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await postProxy('/Auth/CompletePasswordReset', {
        continuationToken: ssprContinuationToken.current,
        newPassword,
      });
      // Auto sign-in with new password
      const email = ssprPendingEmail.current;
      setPasswordExpired(false);
      setSsprStep(null);
      setSsprCodeSentTo(null);
      ssprContinuationToken.current = null;
      ssprPendingEmail.current = null;
      const result = await loginViaProxy(email, newPassword);
      if ('requiresPasswordChange' in result) {
        pendingContinuationToken.current = result.continuation_token;
        setRequiresPasswordChange(true);
      } else if (!('passwordExpired' in result)) {
        applyTokens(result);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Password reset failed.');
    } finally {
      setIsLoading(false);
    }
  }, [applyTokens]);

  const signOut = useCallback(() => {
    clearSession();
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    setRequiresPasswordChange(false);
    setPasswordExpired(false);
    setSsprStep(null);
    setSsprCodeSentTo(null);
    pendingContinuationToken.current = null;
    ssprPendingEmail.current = null;
    ssprContinuationToken.current = null;
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return (
    <AuthContext.Provider value={{
      isAuthenticated: !!user,
      isLoading,
      user,
      accessToken,
      error,
      requiresPasswordChange,
      passwordExpired,
      ssprStep,
      ssprCodeSentTo,
      signIn,
      signInWithBarcode,
      signOut,
      submitNewPassword,
      initiatePasswordReset,
      verifyResetCode,
      completePasswordReset,
      clearError,
    }}
    >
      {children}
    </AuthContext.Provider>
  );
};
